import { convertToParamMap, ParamMap, Params } from '@angular/router';
import { environment } from 'src/environments/environment';

export const URL_CONSTANTS = {
  SPACES: 'spaces',
  SPACES_NEW: 'new',
  HOME: 'home',
  PROFILES: 'profiles',
  ACCOUNT: 'account',
  VERIFY: 'account/verify',
  LOGIN: 'account/login',
  REQUEST_ACCESS: 'request-access',
  WAITING_ROOM: 'waiting-room',
  TECH_CHECK: 'techcheck',
  RECORDINGS: 'analytics/recordings',
  SCHEDULE: 'schedule',
};

export const LANDING_URL = URL_CONSTANTS.HOME;

// The Terms of Service and Privacy Policy the app links to. A build for another brand (Spacebar)
// sets its own in its environment; otherwise they are the Pencil marketing site's pages.
const legalPages = environment as unknown as { termsUrl?: string; privacyUrl?: string };
export const TERMS_URL = legalPages.termsUrl ?? `${environment.marketingUrl}/terms-of-service`;
export const PRIVACY_URL = legalPages.privacyUrl ?? `${environment.marketingUrl}/privacy`;

/**
 * Extracts the space id from a route path, handling the three space URL shapes:
 *   /spaces/:id                     → the space
 *   /spaces/request-access/:id      → access-request flow for :id
 *   /spaces/waiting-room/:id        → waiting-room flow for :id
 * The flow routes share the /spaces/ prefix, so a naive `split('/')[2]` would yield the literal
 * "request-access"/"waiting-room" segment instead of the real id; pull the id from the trailing
 * segment in those cases. Returns '' when the path isn't inside a space.
 *
 * Pass a pathname (no query string or hash) — callers that start from a router url should strip
 * those first.
 */
export const getSpaceIdFromPath = (routePath: string): string => {
  if (!routePath.startsWith(`/${URL_CONSTANTS.SPACES}/`)) {
    return '';
  }

  const segments = routePath.split('/');
  const afterSpaces = segments[2] ?? '';
  if (afterSpaces === URL_CONSTANTS.REQUEST_ACCESS || afterSpaces === URL_CONSTANTS.WAITING_ROOM) {
    return segments[3] ?? '';
  }

  return afterSpaces;
};

export const getSpaceUrl = (spaceId: string): string =>
  `/${URL_CONSTANTS.SPACES}/${encodeURIComponent(spaceId)}`;

/** Schedule deep link to one event occurrence, read by the calendar's `?eventId` handling. */
export const getScheduleEventUrl = (eventId: string): string =>
  `/${URL_CONSTANTS.SCHEDULE}?eventId=${encodeURIComponent(eventId)}`;

export const appendUTMParams = (url: URL, params: ParamMap): URL => {
  params.keys
    .filter((key) => key.startsWith('utm_'))
    .forEach((key) => {
      url.searchParams.append(key, params.get(key) ?? '');
    });
  return url;
};

export const appendReferralParams = (url: URL, params: ParamMap): URL => {
  params.keys
    .filter((key) => key.startsWith('referral_'))
    .forEach((key) => {
      url.searchParams.append(key, params.get(key) ?? '');
    });
  return url;
};

export const extractUTMParams = (params: ParamMap): Record<string, string> => {
  const urlParams: Record<string, string> = {};
  params.keys
    .filter((key) => key.startsWith('utm_'))
    .forEach((key) => {
      urlParams[key] = params.get(key) ?? '';
    });
  return urlParams;
};

export const extractReferralParams = (params: ParamMap): Record<string, string> => {
  const urlParams: Record<string, string> = {};
  params.keys
    .filter((key) => key.startsWith('referral_'))
    .forEach((key) => {
      urlParams[key] = params.get(key) ?? '';
    });
  return urlParams;
};

export const extractTemplateIdParam = (params: ParamMap): Record<string, string> => {
  const urlParams: Record<string, string> = {};
  params.keys
    .filter((key) => key.startsWith('templateid'))
    .forEach((key) => {
      urlParams[key] = params.get(key) ?? '';
    });
  return urlParams;
};

export const queryStringToParamMap = (queryString: string): ParamMap => {
  const paramList = queryString.slice(1).split('&');
  const params: Params = {};
  paramList.forEach((param) => {
    const paramPair = param.split('=');
    params[paramPair[0]] = paramPair[1];
  });
  return convertToParamMap(params);
};

export const appendHttpsIfMissing = (url: string): string =>
  /^https?:\/\//i.test(url) ? url : `https://${url}`;

/** Splits a URL into its non-empty path segments, ignoring query params. */
export const pathSegmentsFromUrl = (url: string): string[] =>
  url.split('?')[0].split('/').filter(Boolean);
